import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Nav";
import { api, errMsg } from "../lib/api";
import { hasAccess } from "../mock";
import { ArrowLeft, RefreshCw, Radar, Sparkles } from "lucide-react";

const STATUS = {
  active: { t: "Active", c: "st-active" }, tp1: { t: "TP1 Hit", c: "st-win" }, tp2: { t: "TP2 Hit", c: "st-win" },
  sl: { t: "Stopped Out", c: "st-loss" }, breakeven: { t: "TP1 · Closed BE", c: "st-win" }, expired: { t: "Expired", c: "st-mute" },
};
const fmt = (v, d) => (typeof v === "number" ? v.toFixed(d) : "—");
const ago = (iso) => {
  if (!iso) return "—";
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  return m < 60 ? `${m}m ago` : m < 1440 ? `${Math.floor(m / 60)}h ago` : `${Math.floor(m / 1440)}d ago`;
};

export const SignalCard = ({ s }) => {
  const st = STATUS[s.status] || STATUS.active;
  return (
    <div className={`scan-card ${s.side === "BUY" ? "buy" : "sell"}`} data-testid="scanner-signal-card">
      <div className="sc-top">
        <div><div className="sc-pair">{s.label}</div><div className="sc-meta">1H · Grade {s.grade}{s.retrace != null ? ` · ICC ${s.retrace}% pullback` : ""} · {ago(s.ts)}</div></div>
        <div className={`sc-side ${s.side === "BUY" ? "up" : "dn"}`} data-testid="scanner-signal-side">{s.side}</div>
      </div>
      <div className="sc-levels">
        {[["Entry", s.entry], ["Stop", s.sl], ["TP1", s.tp1], ["TP2", s.tp2]].map(([k, v]) => (
          <div key={k}><span>{k}</span><b>{fmt(v, s.dec)}</b></div>
        ))}
      </div>
      <div className="sc-conf"><div className="sc-bar"><i style={{ width: `${s.confidence}%` }} /></div><span data-testid="scanner-signal-confidence">{s.confidence}% AI confidence</span></div>
      <p className="sc-why"><Sparkles size={11} /> {s.explanation}</p>
      <div className="sc-foot">
        <div className="sc-factors">{s.factors.filter((f) => f.on !== null).map((f) => <span key={f.name} className={f.on ? "on" : ""}>{f.name}</span>)}</div>
        <span className={`sc-status ${st.c}`} data-testid="scanner-signal-status">{st.t}</span>
      </div>
    </div>
  );
};

const AIScanner = () => {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data: d } = await api.get("/scanner/signals"); setData(d); setErr(""); }
    catch (e) { setErr(errMsg(e, "Could not load signals.")); }
    setBusy(false);
  }, []);

  useEffect(() => {
    if (!hasAccess(user)) return;
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [user, load]);

  if (loading) return <div id="memberDash" />;
  if (!user) return <Navigate to="/" replace />;
  if (!hasAccess(user)) return <Navigate to="/dashboard" replace />;

  const st = data?.stats;
  const cards = [
    ["Signals Today", data ? `${data.today} / ${data.min_per_day}+` : "—"],
    ["30-Day Win Rate", st?.win_rate != null ? `${st.win_rate}%` : "—", st ? `${st.wins}W · ${st.losses}L` : ""],
    ["Active Now", st ? st.active : "—"],
    ["Last Scan", data?.last_scan ? ago(data.last_scan) : "—", data ? `${data.markets} markets · every ${data.scan_minutes}m` : ""],
  ];

  return (
    <div className="admin" data-testid="ai-scanner-page">
      <div className="dash-nav">
        <Logo />
        <div className="dash-nav-r">
          <span className="dash-name">AI Scanner</span>
          <button className="nav-btn nb-login" onClick={() => nav("/dashboard")} data-testid="scanner-back"><ArrowLeft size={12} /> Dashboard</button>
        </div>
      </div>
      <div className="admin-wrap">
        <div className="admin-head">
          <div>
            <div className="section-eye left"><Radar size={11} /> Live · ICC Pattern · 28 FX Pairs · Gold · BTC · ETH</div>
            <h2 className="section-h left">The AI Found<br /><em>Your Next Setups.</em></h2>
          </div>
          <button className="news-refresh" onClick={load} aria-label="Refresh" data-testid="scanner-refresh"><RefreshCw size={13} className={busy ? "spin" : ""} /></button>
        </div>
        <div className="admin-cards">
          {cards.map(([l, v, sub]) => (
            <div className="live-card admin-card" key={l} data-testid={`scanner-stat-${l.split(" ")[0].toLowerCase()}`}>
              <div className="lc-eye">{l}</div><div className="ac-val">{v}</div><div className="ac-sub">{sub}</div>
            </div>
          ))}
        </div>
        {err && <div className="sig-wait" data-testid="scanner-error">{err}</div>}
        <div className="scan-grid">
          {data && data.signals.length === 0 && <div className="live-card sig-wait" data-testid="scanner-empty">The scanner is hunting. New signals appear here automatically — at least 2 every day (forex pauses on weekends; crypto keeps running).</div>}
          {(data?.signals || []).map((s) => <SignalCard key={s.id} s={s} />)}
        </div>
        <p className="live-disc">One signal is live at a time. Every signal must show an Impulse → Correction (38–62%) → Continuation pattern, confirmed by an EMA / MACD / RSI / volume scan on the 1-hour chart and are reviewed by AI. Win rate counts TP1 or better. Educational purposes only. This is not financial advice, and past results do not guarantee future results.</p>
      </div>
    </div>
  );
};

export default AIScanner;
